import { formatDistanceStrict } from 'date-fns';
import { defineMessages } from 'react-intl';
import type { IntlWithFormatter } from '../../contexts/IntlContext';
import { CareExecutionReport } from '../../types';
import { CustomerTransaction } from '../../types/CustomerBalance';
import { CustomerExecutionReport } from '../../types/CustomerExecutionReport';
import { CustomerQuote } from '../../types/CustomerQuote';
import { CustomerTrade } from '../../types/CustomerTrade';
import { ExecutionReport } from '../../types/ExecutionReport';
import { Quote } from '../../types/Quote';
import type { User } from '../../types/User';
import {
  CustomerBalanceTransactionStatusEnum,
  DecisionStatusEnum,
  ExecTypeEnum,
  OrdStatusEnum,
  PricingModeEnum,
  QuoteStatusEnum,
} from '../../types/types';
import { abbreviateId, formattedDateWithMilliseconds } from '../../utils';
import { CustomerTradeSummary } from '../CustomerTradeSummary';
import { CustomerTransactionLabel, CustomerTransactionSummary } from '../CustomerTransactionSummary';
import { ExecutionReportSummary } from '../ExecutionReportSummary';
import { FixingSummary } from '../FixingSummary/FixingSummary';
import { IconName } from '../Icons';
import { QuotesSummary } from '../QuotesSummary';
import { SmartSummary } from '../SmartSummary';
import {
  getCustomerTransactionIconName,
  getTradeStatusIconName,
  getTradeStatusIconVariant,
  getTransactionHistoryIconVariant,
} from '../Status';
import { Text } from '../Text';
import type { TimelineItemProps } from '../Timeline';
import type {
  ExecutionTimelineEntry,
  ExecutionTimelineGroupEntry,
  ExecutionTimelineItemEntry,
  WithTimestamp,
} from './types';

const messages = defineMessages({
  closed: {
    defaultMessage: 'Closed',
    id: 'ExecutionTimeline.closed',
  },
  customer: {
    defaultMessage: 'Customer',
    id: 'ExecutionTimeline.customer',
  },
  customerOrderIdCanceled: {
    defaultMessage: 'Customer order {id} canceled.',
    id: 'ExecutionTimeline.customerOrderIdCanceled',
  },
  customerOrderIdModificationRejected: {
    defaultMessage: 'Customer order #{id} modification rejected.',
    id: 'ExecutionTimeline.customerOrderIdModificationRejected',
  },
  customerOrderIdModified: {
    defaultMessage: 'Customer order #{id} modified.',
    id: 'ExecutionTimeline.customerOrderIdModified',
  },
  customerOrderIdPaused: {
    defaultMessage: 'Customer order #{id} paused.',
    id: 'ExecutionTimeline.customerOrderIdPaused',
  },
  customerOrderIdPauseRequested: {
    defaultMessage: 'Customer order #{id} pause requested.',
    id: 'ExecutionTimeline.customerOrderIdPauseRequested',
  },
  customerOrderIdRejected: {
    defaultMessage: 'Customer order #{id} rejected.',
    id: 'ExecutionTimeline.customerOrderIdRejected',
  },
  customerOrderIdResumed: {
    defaultMessage: 'Customer order #{id} resumed.',
    id: 'ExecutionTimeline.customerOrderIdResumed',
  },
  customerOrderIdResumeRequested: {
    defaultMessage: 'Customer order #{id} resume requested.',
    id: 'ExecutionTimeline.customerOrderIdResumeRequested',
  },
  customerOrderIdSubmitted: {
    defaultMessage: 'Customer order #{id} submitted.',
    id: 'ExecutionTimeline.customerOrderIdSubmitted',
  },
  customerQuoteIdReceived: {
    defaultMessage: 'Customer quote #{id} received.',
    id: 'ExecutionTimeline.customerQuoteIdReceived',
  },
  customerQuoteIdRejected: {
    defaultMessage: 'Customer quote #{id} rejected.',
    id: 'ExecutionTimeline.customerQuoteIdRejected',
  },
  customerQuoteIdCanceled: {
    defaultMessage: 'Customer quote #{id} canceled.',
    id: 'ExecutionTimeline.customerQuoteIdCanceled',
  },
  customerQuoteIdPendingFixing: {
    defaultMessage: 'Customer quote #{id} pending fixing.',
    id: 'ExecutionTimeline.customerQuoteIdPendingFixing',
  },
  customerQuoteIdUpdated: {
    defaultMessage: 'Customer quote #{id} updated.',
    id: 'ExecutionTimeline.customerQuoteIdUpdated',
  },
  fixingAppliedOnCustomerRFQId: {
    defaultMessage: 'Fixing applied on Customer RFQ #{id}.',
    id: 'ExecutionTimeline.fixingAppliedOnCustomerRFQId',
  },
  dealer: {
    defaultMessage: 'Dealer',
    id: 'ExecutionTimeline.dealer',
  },
  newCounterOffer: {
    defaultMessage: 'New counter offer.',
    id: 'ExecutionTimeline.newCounterOffer',
  },
  newFirmRequest: {
    defaultMessage: 'New firm request.',
    id: 'ExecutionTimeline.newFirmRequest',
  },
  newLoan: {
    defaultMessage: 'New loan',
    id: 'ExecutionTimeline.newLoan',
  },
  newOffer: {
    defaultMessage: 'New offer.',
    id: 'ExecutionTimeline.newOffer',
  },
  newRequest: {
    defaultMessage: 'New request.',
    id: 'ExecutionTimeline.newRequest',
  },
  newTypeTransaction: {
    defaultMessage: 'New {type, select, Principal {Principal} other {Collateral}} Transaction',
    id: 'ExecutionTimeline.newTypeTransaction',
  },
  orderIdCanceled: {
    defaultMessage: 'Order #{id} canceled.',
    id: 'ExecutionTimeline.orderIdCanceled',
  },
  orderIdModificationRejected: {
    defaultMessage: 'Order #{id} modification rejected.',
    id: 'ExecutionTimeline.orderIdModificationRejected',
  },
  orderIdModified: {
    defaultMessage: 'Order #{id} modified.',
    id: 'ExecutionTimeline.orderIdModified',
  },
  orderIdPaused: {
    defaultMessage: 'Order #{id} paused.',
    id: 'ExecutionTimeline.orderIdPaused',
  },
  orderIdPauseRequested: {
    defaultMessage: 'Order #{id} pause requested.',
    id: 'ExecutionTimeline.orderIdPauseRequested',
  },
  orderIdRejected: {
    defaultMessage: 'Order #{id} rejected.',
    id: 'ExecutionTimeline.orderIdRejected',
  },
  orderIdResumed: {
    defaultMessage: 'Order #{id} resumed.',
    id: 'ExecutionTimeline.orderIdResumed',
  },
  orderIdResumeRequested: {
    defaultMessage: 'Order #{id} resume requested.',
    id: 'ExecutionTimeline.orderIdResumeRequested',
  },
  orderIdSubmitted: {
    defaultMessage: 'Order #{id} submitted.',
    id: 'ExecutionTimeline.orderIdSubmitted',
  },
  careOrderIdCanceled: {
    defaultMessage: 'Care Order #{id} canceled.',
    id: 'ExecutionTimeline.careOrderIdCanceled',
  },
  careOrderIdModificationRejected: {
    defaultMessage: 'Care Order #{id} modification rejected.',
    id: 'ExecutionTimeline.careOrderIdModificationRejected',
  },
  careOrderIdCancellationRejected: {
    defaultMessage: 'Care Order #{id} cancellation rejected.',
    id: 'ExecutionTimeline.careOrderIdCancellationRejected',
  },
  careOrderIdCancelRejected: {
    defaultMessage: 'Care Order #{id} cancel rejected.',
    id: 'ExecutionTimeline.careOrderIdCancelRejected',
  },
  careOrderIdModified: {
    defaultMessage: 'Care Order #{id} modified.',
    id: 'ExecutionTimeline.careOrderIdModified',
  },
  careOrderIdRejected: {
    defaultMessage: 'Care Order #{id} rejected.',
    id: 'ExecutionTimeline.careOrderIdRejected',
  },
  careOrderIdPendingNew: {
    defaultMessage: 'Care Order #{id} received.',
    id: 'ExecutionTimeline.careOrderIdPendingNew',
  },
  careOrderIdPendingActivation: {
    defaultMessage: 'Care Order #{id} {action, select, modified {modified} other {created}}, pending approval.',
    id: 'ExecutionTimeline.careOrderIdPendingActivation',
  },
  careOrderIdNew: {
    defaultMessage: 'Care Order #{id} accepted.',
    id: 'ExecutionTimeline.careOrderIdNew',
  },
  careOrderIdTradedOn: {
    defaultMessage: 'Traded on Care Order #{id}.',
    id: 'ExecutionTimeline.careOrderIdTradedOn',
  },
  careOrderIdChildOrdersTerminal: {
    defaultMessage: 'Care Order #{id} finished - all trades confirmed.',
    id: 'ExecutionTimeline.careOrderIdChildOrdersTerminal',
  },
  pendingTypeTransfer: {
    defaultMessage: 'Pending {type, select, Principal {Principal} other {Collateral}} Transfer',
    id: 'ExecutionTimeline.pendingTypeTransfer',
  },
  processingTypeTransfer: {
    defaultMessage: 'Processing {type, select, Principal {Principal} other {Collateral}} Transfer',
    id: 'ExecutionTimeline.processingTypeTransfer',
  },
  rfqIdSubmitted: {
    defaultMessage: 'RFQ #{id} submitted.',
    id: 'ExecutionTimeline.rfqIdSubmitted',
  },
  rfqIdRejected: {
    defaultMessage: 'RFQ #{id} rejected.',
    id: 'ExecutionTimeline.rfqIdRejected',
  },
  rfqIdCanceled: {
    defaultMessage: 'RFQ #{id} canceled.',
    id: 'ExecutionTimeline.rfqIdCanceled',
  },
  rfqIdPendingFixing: {
    defaultMessage: 'RFQ #{id} pending fixing.',
    id: 'ExecutionTimeline.rfqIdPendingFixing',
  },
  fixingAppliedOnRFQId: {
    defaultMessage: 'Fixing applied on RFQ #{id}.',
    id: 'ExecutionTimeline.fixingAppliedOnRFQId',
  },
  rfqIdUpdated: {
    defaultMessage: 'Quotes updated on RFQ #{id}.',
    id: 'ExecutionTimeline.rfqIdUpdated',
  },
  receivedError: {
    defaultMessage: 'Received error.',
    id: 'ExecutionTimeline.receivedError',
  },
  requestAccepted: {
    defaultMessage: 'Request accepted.',
    id: 'ExecutionTimeline.requestAccepted',
  },
  requestCanceled: {
    defaultMessage: 'Request canceled.',
    id: 'ExecutionTimeline.requestCanceled',
  },
  requestRejected: {
    defaultMessage: 'Request rejected.',
    id: 'ExecutionTimeline.requestRejected',
  },
  sourceStatusCustomerTrade: {
    defaultMessage:
      '{source, select, Market {Market} User {User} Dealer {Dealer} DealerUser {DealerUser} other { }} {status, select, Confirmed {Confirmed} Pending {Pending} other {Canceled}} Customer Trade.',
    id: 'ExecutionTimeline.sourceStatusCustomerTrade',
  },
  termSheetComplete: {
    defaultMessage: 'termSheetComplete',
    id: 'ExecutionTimeline.termSheetComplete',
  },
  termSheetReadyForSignature: {
    defaultMessage: 'termSheetReadyForSignature',
    id: 'ExecutionTimeline.termSheetReadyForSignature',
  },
  termSheetRequested: {
    defaultMessage: 'termSheetRequested',
    id: 'ExecutionTimeline.termSheetRequested',
  },
  tradedOnCustomerOrderId: {
    defaultMessage: 'Traded on customer order #{id}.',
    id: 'ExecutionTimeline.tradedOnCustomerOrderId',
  },
  tradedOnOrderId: {
    defaultMessage: 'Traded on order #{id}.',
    id: 'ExecutionTimeline.tradedOnOrderId',
  },
  typeTransferCompleted: {
    defaultMessage: '{type, select, Principal {Principal} other {Collateral}} Transfer Completed',
    id: 'ExecutionTimeline.typeTransferCompleted',
  },
  typeTransferRejected: {
    defaultMessage: '{type, select, Principal {Principal} other {Collateral}} Transfer Rejected',
    id: 'ExecutionTimeline.typeTransferRejected',
  },
  typeTransferSubmitted: {
    defaultMessage: '{type, select, Principal {Principal} other {Collateral}} Transfer Submitted',
    id: 'ExecutionTimeline.typeTransferSubmitted',
  },
});

export function getCustomerExecutionReportItem(
  executionReport: CustomerExecutionReport,
  intl: IntlWithFormatter
): Partial<TimelineItemProps> | undefined {
  // We don't seem to get execution reports after an order has been canceled, so have to check OrdStatus instead
  if (executionReport.OrdStatus === OrdStatusEnum.Canceled) {
    return {
      label: intl.formatMessage(messages.customerOrderIdCanceled, { id: abbreviateId(executionReport.OrderID) }),
      icon: IconName.MinusCircle,
      variant: 'primary',
      content: executionReport.Text,
    };
  }

  switch (executionReport.ExecType) {
    case ExecTypeEnum.Rejected:
      return {
        label: intl.formatMessage(messages.customerOrderIdRejected, { id: abbreviateId(executionReport.OrderID) }),
        icon: IconName.ExclamationCircle,
        variant: 'warning',
        content: (
          <Text lineHeight={1.5}>
            {executionReport.Text}
            {executionReport.Text && executionReport.InternalText && <br />}
            {executionReport.InternalText && (
              <>
                <strong>Internal Reason:</strong> {executionReport.InternalText}
              </>
            )}
          </Text>
        ),
      };
    case ExecTypeEnum.PendingNew:
      return {
        label: intl.formatMessage(messages.customerOrderIdSubmitted, { id: abbreviateId(executionReport.OrderID) }),
        content: <ExecutionReportSummary executionReport={executionReport} />,
      };
    case ExecTypeEnum.PendingPause:
      return {
        label: intl.formatMessage(messages.customerOrderIdPauseRequested, {
          id: abbreviateId(executionReport.OrderID),
        }),
      };
    case ExecTypeEnum.Paused:
      return {
        label: intl.formatMessage(messages.customerOrderIdPaused, { id: abbreviateId(executionReport.OrderID) }),
        icon: IconName.PauseCircle,
      };
    case ExecTypeEnum.PendingResume:
      return {
        label: intl.formatMessage(messages.customerOrderIdResumeRequested, {
          id: abbreviateId(executionReport.OrderID),
        }),
      };
    case ExecTypeEnum.Resumed:
      return {
        label: intl.formatMessage(messages.customerOrderIdResumed, { id: abbreviateId(executionReport.OrderID) }),
      };
    case ExecTypeEnum.Replaced:
      return {
        label: intl.formatMessage(messages.customerOrderIdModified, { id: abbreviateId(executionReport.OrderID) }),
        content: <ExecutionReportSummary executionReport={executionReport} />,
        variant: 'primary',
        icon: IconName.Pencil,
      };
    case ExecTypeEnum.ReplaceRejected:
      return {
        label: intl.formatMessage(messages.customerOrderIdModificationRejected, {
          id: abbreviateId(executionReport.OrderID),
        }),
        icon: IconName.ExclamationCircle,
        variant: 'warning',
        content: executionReport.Text,
      };
    case ExecTypeEnum.Trade:
      switch (executionReport.OrdStatus) {
        case OrdStatusEnum.Filled:
          return {
            label: intl.formatMessage(messages.tradedOnCustomerOrderId, { id: abbreviateId(executionReport.OrderID) }),
            icon: IconName.CheckCircle,
            variant: 'positive',
            content: <SmartSummary entity={executionReport} type="order" qtyField="LastQty" priceField="LastPx" />,
          };
        default: {
          return {
            label: intl.formatMessage(messages.tradedOnCustomerOrderId, { id: abbreviateId(executionReport.OrderID) }),
            content: <SmartSummary entity={executionReport} type="order" qtyField="LastQty" priceField="LastPx" />,
          };
        }
      }
  }
}

export function getExecutionReportItem(
  executionReport: ExecutionReport,
  intl: IntlWithFormatter,
  prevExecutionReport?: ExecutionReport
): Partial<TimelineItemProps> | undefined {
  // We don't seem to get execution reports after an order has been canceled, so have to check OrdStatus instead
  if (executionReport.OrdStatus === OrdStatusEnum.Canceled) {
    return {
      label: intl.formatMessage(messages.orderIdCanceled, { id: abbreviateId(executionReport.OrderID) }),
      icon: IconName.MinusCircle,
      variant: 'primary',
      content: executionReport.Text,
      initiator: executionReport.RequestUser ?? 'System',
    };
  }

  switch (executionReport.ExecType) {
    case ExecTypeEnum.Rejected:
      return {
        label: intl.formatMessage(messages.orderIdRejected, { id: abbreviateId(executionReport.OrderID) }),
        icon: IconName.ExclamationCircle,
        variant: 'warning',
        content: executionReport.Text,
      };
    case ExecTypeEnum.PendingNew:
      return {
        label: intl.formatMessage(messages.orderIdSubmitted, { id: abbreviateId(executionReport.OrderID) }),
        content:
          executionReport.PricingMode === PricingModeEnum.SpreadToFixing ? (
            <SmartSummary
              entity={executionReport}
              type="order"
              qtyField="OrderQty"
              priceField="LastPx"
              showLastMarket={true}
            />
          ) : (
            <ExecutionReportSummary executionReport={executionReport} />
          ),
        initiator: executionReport.RequestUser,
      };
    case ExecTypeEnum.PendingPause:
      return {
        label: intl.formatMessage(messages.orderIdPauseRequested, { id: abbreviateId(executionReport.OrderID) }),
        initiator: executionReport.RequestUser ?? 'System',
      };
    case ExecTypeEnum.Paused:
      return {
        label: intl.formatMessage(messages.orderIdPaused, { id: abbreviateId(executionReport.OrderID) }),
        icon: IconName.PauseCircle,
        variant: 'default',
      };
    case ExecTypeEnum.PendingResume:
      return {
        label: intl.formatMessage(messages.orderIdResumeRequested, { id: abbreviateId(executionReport.OrderID) }),
        initiator: executionReport.RequestUser ?? 'System',
      };
    case ExecTypeEnum.Resumed:
      return {
        label: intl.formatMessage(messages.orderIdResumed, { id: abbreviateId(executionReport.OrderID) }),
      };
    case ExecTypeEnum.Replaced:
      return {
        label: intl.formatMessage(messages.orderIdModified, { id: abbreviateId(executionReport.OrderID) }),
        content: (
          <ExecutionReportSummary
            showDiff={!!prevExecutionReport}
            executionReport={executionReport}
            prevExecutionReport={prevExecutionReport}
          />
        ),
        variant: 'primary',
        icon: IconName.Pencil,
        initiator: executionReport.RequestUser ?? 'System',
      };
    case ExecTypeEnum.ReplaceRejected:
      return {
        label: intl.formatMessage(messages.orderIdModificationRejected, { id: abbreviateId(executionReport.OrderID) }),
        icon: IconName.ExclamationCircle,
        variant: 'warning',
        content: executionReport.Text,
      };
    case ExecTypeEnum.Triggered:
      return {
        label: `Order ${abbreviateId(executionReport.OrderID)} triggered.`,
        icon: IconName.ChartLine,
        variant: 'default',
        content: executionReport.Text,
      };
    case ExecTypeEnum.Trade:
      switch (executionReport.OrdStatus) {
        case OrdStatusEnum.Filled:
          return {
            label: intl.formatMessage(messages.tradedOnOrderId, { id: abbreviateId(executionReport.OrderID) }),
            icon: IconName.CheckCircle,
            variant: 'positive',
            content: (
              <SmartSummary
                entity={executionReport}
                type="order"
                qtyField="LastQty"
                priceField="LastPx"
                showLastMarket={true}
              />
            ),
          };
        default: {
          return {
            label: intl.formatMessage(messages.tradedOnOrderId, { id: abbreviateId(executionReport.OrderID) }),
            content: (
              <SmartSummary
                entity={executionReport}
                type="order"
                qtyField="LastQty"
                priceField="LastPx"
                showLastMarket={true}
              />
            ),
          };
        }
      }
  }
}

export function getCareExecutionReportItem(
  executionReport: CareExecutionReport,
  intl: IntlWithFormatter,
  prevExecutionReport?: CareExecutionReport
): Partial<TimelineItemProps> | undefined {
  switch (executionReport.ExecType) {
    case ExecTypeEnum.PendingNew:
      return {
        icon: IconName.CircleDotted,
        variant: 'notification',
        label: intl.formatMessage(messages.careOrderIdPendingNew, { id: abbreviateId(executionReport.OrderID) }),
        content: executionReport.Text,
        initiator: executionReport.RequestUser,
      };
    case ExecTypeEnum.New:
    case ExecTypeEnum.Restated:
    case ExecTypeEnum.Replaced: {
      if (!(executionReport.OrdStatus === OrdStatusEnum.New || executionReport.OrdStatus === OrdStatusEnum.Replaced)) {
        // Restatements can happen on many statuses, but we only care about New and Replaced (modification)
        return undefined;
      }

      const isCreation =
        executionReport.ExecType === ExecTypeEnum.New ||
        (executionReport.ExecType === ExecTypeEnum.Restated && prevExecutionReport?.ExecType === ExecTypeEnum.New);

      const isModification =
        executionReport.ExecType === ExecTypeEnum.Replaced ||
        (executionReport.ExecType === ExecTypeEnum.Restated && prevExecutionReport?.ExecType === ExecTypeEnum.Replaced);

      // For new COs and modified COs, if they first come into a state of PendingActivation (pending approval), we show that first.
      if (executionReport.DecisionStatus === DecisionStatusEnum.PendingActivation) {
        return {
          icon: IconName.CircleDotted,
          variant: 'notification',
          label: intl.formatMessage(messages.careOrderIdPendingActivation, {
            id: abbreviateId(executionReport.OrderID),
            action: isModification ? 'modified' : 'created',
          }),
          initiator: executionReport.RequestUser,
        };
      }

      // Else down here, we are now approved for trading - we can show either the New or Modified content
      if (isCreation) {
        return {
          icon: IconName.CircleSolid,
          variant: 'notification',
          label: intl.formatMessage(messages.careOrderIdNew, { id: abbreviateId(executionReport.OrderID) }),
          content: <ExecutionReportSummary executionReport={executionReport} />,
          initiator: executionReport.RequestUser,
        };
      } else if (isModification) {
        return {
          label: intl.formatMessage(messages.careOrderIdModified, { id: abbreviateId(executionReport.OrderID) }),
          content: (
            <ExecutionReportSummary
              showDiff={!!prevExecutionReport}
              executionReport={executionReport}
              prevExecutionReport={prevExecutionReport}
            />
          ),
          variant: 'primary',
          icon: IconName.Pencil,
          initiator: executionReport.RequestUser ?? 'System',
        };
      }

      return undefined;
    }

    case ExecTypeEnum.Rejected:
      return {
        label: intl.formatMessage(messages.careOrderIdRejected, { id: abbreviateId(executionReport.OrderID) }),
        icon: IconName.ExclamationCircle,
        variant: 'warning',
        content: executionReport.Text,
      };
    case ExecTypeEnum.Canceled:
      return {
        label: intl.formatMessage(messages.careOrderIdCanceled, { id: abbreviateId(executionReport.OrderID) }),
        content: executionReport.Text,
        icon: IconName.MinusCircle,
        variant: 'primary',
        initiator: executionReport.RequestUser ?? 'System',
      };

    case ExecTypeEnum.ReplaceRejected:
      return {
        label: intl.formatMessage(messages.careOrderIdModificationRejected, {
          id: abbreviateId(executionReport.OrderID),
        }),
        icon: IconName.ExclamationCircle,
        variant: 'warning',
        content: executionReport.Text,
      };
    case ExecTypeEnum.CancelRejected:
      return {
        label: intl.formatMessage(messages.careOrderIdCancellationRejected, {
          id: abbreviateId(executionReport.OrderID),
        }),
        icon: IconName.ExclamationCircle,
        variant: 'warning',
        content: executionReport.Text,
      };
    case ExecTypeEnum.Trade:
      return {
        label: intl.formatMessage(messages.careOrderIdTradedOn, { id: abbreviateId(executionReport.OrderID) }),
        icon: IconName.CheckCircle,
        variant: executionReport.OrdStatus === OrdStatusEnum.Filled ? 'positive' : undefined,
        content: (
          <SmartSummary
            entity={executionReport}
            type="order"
            qtyField="LastQty"
            priceField="LastPx"
            showLastMarket={true}
          />
        ),
      };
    case ExecTypeEnum.DoneForDay:
      return {
        label: intl.formatMessage(messages.careOrderIdChildOrdersTerminal, {
          id: abbreviateId(executionReport.OrderID),
        }),
        icon: IconName.CheckCircleSolid,
        variant: 'positive',
      };
  }
}

export function getQuoteItem(quote: Quote, intl: IntlWithFormatter): Partial<TimelineItemProps> | undefined {
  if (quote.PricingMode === PricingModeEnum.SpreadToFixing) {
    // We only have history for SpreadToFixing type quotes and customer quotes
    switch (quote.QuoteStatus) {
      case QuoteStatusEnum.PendingNew:
        return {
          label: intl.formatMessage(messages.rfqIdSubmitted, { id: abbreviateId(quote.RFQID) }),
          timestamp: quote.SubmitTime,
          content: <QuotesSummary quote={quote} />,
        };
      case QuoteStatusEnum.Canceled:
        return {
          label: intl.formatMessage(messages.rfqIdCanceled, { id: abbreviateId(quote.RFQID) }),
          timestamp: quote.Timestamp,
          content: <QuotesSummary quote={quote} />,
        };
      case QuoteStatusEnum.Rejected:
        return {
          label: intl.formatMessage(messages.rfqIdRejected, { id: abbreviateId(quote.RFQID) }),
          timestamp: quote.Timestamp,
          content: <QuotesSummary quote={quote} />,
        };
      case QuoteStatusEnum.Filled:
        return {
          label: intl.formatMessage(messages.fixingAppliedOnRFQId, { id: abbreviateId(quote.RFQID) }),
          timestamp: quote.Timestamp,
          content: <FixingSummary entity={quote} />,
          icon: IconName.CheckCircleSolid,
          variant: 'positive',
        };
      case QuoteStatusEnum.PendingFix:
        return {
          label: intl.formatMessage(messages.rfqIdPendingFixing, { id: abbreviateId(quote.RFQID) }),
          timestamp: quote.Timestamp,
          content: <FixingSummary entity={quote} />,
        };
      case QuoteStatusEnum.Open:
        return {
          label: intl.formatMessage(messages.rfqIdUpdated, { id: abbreviateId(quote.RFQID) }),
          timestamp: quote.Timestamp,
          content: <QuotesSummary quote={quote} />,
        };
      default:
        return undefined;
    }
  }
  return {
    label: intl.formatMessage(messages.rfqIdUpdated, { id: abbreviateId(quote.RFQID) }),
    timestamp: quote.SubmitTime,
    content: <QuotesSummary quote={quote} />,
  };
}

export function getCustomerQuoteItem(
  quote: CustomerQuote,
  intl: IntlWithFormatter
): Partial<TimelineItemProps> | undefined {
  if (quote.PricingMode === PricingModeEnum.SpreadToFixing) {
    switch (quote.QuoteStatus) {
      case QuoteStatusEnum.PendingNew:
        return {
          label: intl.formatMessage(messages.customerQuoteIdReceived, { id: abbreviateId(quote.RFQID) }),
          timestamp: quote.SubmitTime,
          content: <QuotesSummary quote={quote} />,
        };
      case QuoteStatusEnum.Canceled:
        return {
          label: intl.formatMessage(messages.customerQuoteIdCanceled, { id: abbreviateId(quote.RFQID) }),
          timestamp: quote.Timestamp,
          content: <QuotesSummary quote={quote} />,
        };
      case QuoteStatusEnum.Rejected:
        return {
          label: intl.formatMessage(messages.customerQuoteIdRejected, { id: abbreviateId(quote.RFQID) }),
          timestamp: quote.Timestamp,
          content: <QuotesSummary quote={quote} />,
        };
      case QuoteStatusEnum.Filled:
        return {
          label: intl.formatMessage(messages.fixingAppliedOnCustomerRFQId, { id: abbreviateId(quote.RFQID) }),
          timestamp: quote.Timestamp,
          content: <FixingSummary entity={quote} />,
          icon: IconName.CheckCircleSolid,
          variant: 'positive',
        };
      case QuoteStatusEnum.PendingFix:
        return {
          label: intl.formatMessage(messages.customerQuoteIdPendingFixing, { id: abbreviateId(quote.RFQID) }),
          timestamp: quote.Timestamp,
          content: <FixingSummary entity={quote} />,
        };
      case QuoteStatusEnum.Open:
        return {
          label: intl.formatMessage(messages.customerQuoteIdUpdated, { id: abbreviateId(quote.RFQID) }),
          timestamp: quote.Timestamp,
          content: <QuotesSummary quote={quote} />,
        };
      default:
        return undefined;
    }
  }
  return {
    label: intl.formatMessage(messages.customerQuoteIdReceived, { id: abbreviateId(quote.RFQID) }),
    timestamp: quote.SubmitTime,
    content: <QuotesSummary quote={quote} />,
  };
}

export function getCustomerTradeItem(
  customerTrade: CustomerTrade,
  intl: IntlWithFormatter
): Partial<TimelineItemProps> | undefined {
  return {
    label: intl.formatMessage(messages.sourceStatusCustomerTrade, {
      source: customerTrade.TradeSource ?? '',
      status: customerTrade.TradeStatus,
    }),
    timestamp: customerTrade.TransactTime,
    icon: getTradeStatusIconName(customerTrade.TradeStatus),
    variant: getTradeStatusIconVariant(customerTrade.TradeStatus),
    content: <CustomerTradeSummary entity={customerTrade} />,
  };
}

export function getCustomerTransactionItem(
  customerTransaction: CustomerTransaction,
  intl: IntlWithFormatter
): Partial<TimelineItemProps> | undefined {
  return {
    label: <CustomerTransactionLabel entity={customerTransaction} />,
    timestamp: customerTransaction.Timestamp,
    icon: getCustomerTransactionIconName(customerTransaction.Status),
    variant: getTransactionHistoryIconVariant(customerTransaction.Status),
    content: <CustomerTransactionSummary entity={customerTransaction} />,
  };
}

export function getTimelineItemData<T extends { Timestamp: string }>(
  item: T,
  intl: IntlWithFormatter,
  showInitiatorLabels?: boolean,
  user?: User,
  prevItem?: T
): Partial<TimelineItemProps> | undefined {
  let itemData: Partial<TimelineItemProps> | undefined = undefined;
  if (item instanceof CustomerExecutionReport) {
    itemData = getCustomerExecutionReportItem(item, intl);
  } else if (item instanceof ExecutionReport) {
    itemData = getExecutionReportItem(item, intl, prevItem instanceof ExecutionReport ? prevItem : undefined);
  } else if (item instanceof Quote) {
    itemData = getQuoteItem(item, intl);
  } else if (item instanceof CustomerQuote) {
    itemData = getCustomerQuoteItem(item, intl);
  } else if (item instanceof CustomerTrade) {
    itemData = getCustomerTradeItem(item, intl);
  } else if (item instanceof CustomerTransaction) {
    itemData = getCustomerTransactionItem(item, intl);
  } else if (item instanceof CareExecutionReport) {
    itemData = getCareExecutionReportItem(item, intl, prevItem instanceof CareExecutionReport ? prevItem : undefined);
  }

  if (itemData != null && itemData.timestamp == null) {
    itemData.timestamp = item.Timestamp;
  }

  if (itemData != null && itemData.revision == null && 'Revision' in item && typeof item.Revision === 'number') {
    itemData.revision = item.Revision;
  }

  if (itemData != null && showInitiatorLabels) {
    if (item instanceof CustomerQuote || item instanceof CustomerExecutionReport) {
      itemData.initiator = intl.formatMessage(messages.customer);
    } else if (item instanceof ExecutionReport || item instanceof Quote) {
      itemData.initiator = intl.formatMessage(messages.dealer);
    } else if (item instanceof CustomerTransaction) {
      // Show the specific user who performed each action
      if (item.InitiatorUser) {
        itemData.initiator = item.InitiatorUser;
      } else if (item.DealerUser) {
        itemData.initiator = item.DealerUser;
      } else if (item.TreasuryUser) {
        itemData.initiator = item.TreasuryUser;
      } else if (
        item.Status === CustomerBalanceTransactionStatusEnum.TransferInitiated ||
        item.Status === CustomerBalanceTransactionStatusEnum.TransferRegistered ||
        item.Status === CustomerBalanceTransactionStatusEnum.TransferPendingBlockchainConfirmation
      ) {
        itemData.initiator = 'System';
      }
    }
  }

  return itemData == null ? undefined : itemData;
}

export function getTimestampText(firstTimestamp?: string, currentTimestamp?: string): string {
  if (currentTimestamp == null) {
    return '';
  }
  if (firstTimestamp == null) {
    return formattedDateWithMilliseconds(currentTimestamp) ?? '';
  }

  const timestampDiff = formatDistanceStrict(Sugar.Date.create(currentTimestamp), Sugar.Date.create(firstTimestamp));
  const timestamp = formattedDateWithMilliseconds(currentTimestamp);
  return timestampDiff === '0 seconds' ? (timestamp ?? '') : `${timestamp} (${timestampDiff})`;
}

export function groupSimilarTimelineItems<T extends WithTimestamp>(
  sortedItems: ExecutionTimelineItemEntry<T>[]
): ExecutionTimelineEntry<T>[] {
  let currentGroup: ExecutionTimelineGroupEntry<T> | null = null;
  const groupedItems: ExecutionTimelineEntry<T>[] = [];

  // For now we'll just group trades
  for (const item of sortedItems) {
    if (
      (item.rawData instanceof ExecutionReport || item.rawData instanceof CustomerExecutionReport) &&
      item.rawData.ExecType === ExecTypeEnum.Trade &&
      item.rawData.OrdStatus !== OrdStatusEnum.Filled
    ) {
      if (currentGroup == null) {
        currentGroup = {
          type: 'group',
          items: [],
        };
      }
      currentGroup.items.push(item);
    } else {
      if (currentGroup != null) {
        groupedItems.push({
          ...currentGroup,
        });
        currentGroup = null;
      }
      groupedItems.push(item);
    }
  }

  if (currentGroup != null) {
    groupedItems.push(currentGroup);
  }

  return groupedItems;
}

/**
 * Get the sort priority for a CustomerTransaction status.
 * Lower numbers come first in the timeline.
 */
function getCustomerTransactionStatusPriority(status: CustomerBalanceTransactionStatusEnum): number {
  const priorities: Record<CustomerBalanceTransactionStatusEnum, number> = {
    New: 0,
    PendingApproval: 1,
    PendingTransfer: 2,
    TransferInitiated: 3,
    TransferRegistered: 4,
    TransferPendingBlockchainConfirmation: 5,
    Completed: 6,
    Rejected: 7,
    Canceled: 8,
  };
  return priorities[status] ?? 999;
}

/**
 * Sorts timeline items according to the `Timestamp` of the item.
 * Orders dealer events before customer events when timestamps are identical.
 * Orders CustomerTransaction statuses by logical workflow order when timestamps are identical.
 * @param items
 */
export function sortTimelineItems<T extends WithTimestamp>(
  items: ExecutionTimelineItemEntry<T>[],
  reverse?: boolean
): ExecutionTimelineItemEntry<T>[] {
  const sortedItems = [...items].toSorted((a, b) => {
    const timestampSort = a.props.timestamp.localeCompare(b.props.timestamp);
    if (timestampSort !== 0) {
      // If there is a timestamp diff, always let that determine the sorting order
      return timestampSort;
    }

    // Else below here, we have the same timestamp. In some timelines, we have different types of execution reports
    // combined into one timeline. In business terms, always showing one before the other makes the most sense.

    // Handle ExecutionReport vs CustomerExecutionReport ordering
    if (a.rawData instanceof ExecutionReport && b.rawData instanceof CustomerExecutionReport) {
      return -1;
    }
    if (a.rawData instanceof CustomerExecutionReport && b.rawData instanceof ExecutionReport) {
      return 1;
    }

    // Handle CustomerTransaction status ordering
    if (a.rawData instanceof CustomerTransaction && b.rawData instanceof CustomerTransaction) {
      const priorityA = getCustomerTransactionStatusPriority(a.rawData.Status);
      const priorityB = getCustomerTransactionStatusPriority(b.rawData.Status);
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
    }

    // Else, there is no special business logic case. We just try to do the tie-break by revision.
    const revisionSort = (a.props.revision ?? 0) - (b.props.revision ?? 0);
    return revisionSort;
  });
  return reverse ? sortedItems.reverse() : sortedItems;
}

/*
 * Flatten groups that are supposed to be open
 * @param items
 * @param openedGroups
 */
export function flattenOpenedGroups<T extends WithTimestamp>(
  items: ExecutionTimelineEntry<T>[],
  openedGroups: Set<number>
): ExecutionTimelineEntry<T>[] {
  if (openedGroups.size === 0) {
    return items;
  }
  return items.reduce((acc: ExecutionTimelineEntry<T>[], item, index) => {
    if (openedGroups.has(index) && item.type === 'group') {
      acc = acc.concat(item.items);
    } else {
      acc.push(item);
    }
    return acc;
  }, []);
}
